import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { validateSession, logoutSession, getCurrentSessionToken } from '@/lib/sessionManager';
import { toast } from 'sonner';

export function useSessionValidator() {
  const [isValidating, setIsValidating] = useState(true);
  const [isSessionValid, setIsSessionValid] = useState(true);
  const navigate = useNavigate();
  const isHandlingInvalidRef = useRef(false);

  const handleInvalidSession = useCallback(async () => {
    // Prevent multiple simultaneous logout attempts
    if (isHandlingInvalidRef.current) return;
    isHandlingInvalidRef.current = true;

    console.log('[useSessionValidator] Session invalidated - logging out');
    
    // Clear local session
    await logoutSession();
    
    // Sign out from Supabase
    await supabase.auth.signOut();
    
    // Show message
    toast.error('Session Expired', {
      description: 'You have been logged out because you logged in on another device or your session expired.',
      duration: 5000
    });
    
    // Navigate to login
    navigate('/auth');
    
    // Reset flag after a delay
    setTimeout(() => {
      isHandlingInvalidRef.current = false;
    }, 5000);
  }, [navigate]);

  useEffect(() => {
    let mounted = true;
    let realtimeChannel: ReturnType<typeof supabase.channel> | null = null;

    const validate = async () => {
      // Check if user is logged in first
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        if (mounted) {
          setIsValidating(false);
          setIsSessionValid(false);
        }
        return;
      }

      // Verify the user still exists in auth (handles deleted users)
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        console.log('[useSessionValidator] User no longer exists in auth - logging out');
        if (mounted) {
          handleInvalidSession();
        }
        return;
      }

      // Only validate if we have a session token
      const sessionToken = getCurrentSessionToken();
      if (!sessionToken) {
        // No session token but user is logged in - backwards compatibility
        // Don't invalidate - user logged in before session management feature
        if (mounted) {
          setIsValidating(false);
          setIsSessionValid(true);
        }
        return;
      }

      const isValid = await validateSession();
      
      if (!mounted) return;

      // If invalid, retry once after a short delay before logging out
      // This handles transient network issues
      if (!isValid) {
        console.log('[useSessionValidator] First validation failed, retrying in 2 seconds...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        if (!mounted) return;
        
        const retryValid = await validateSession();
        if (!retryValid) {
          setIsSessionValid(false);
          setIsValidating(false);
          handleInvalidSession();
          return;
        }
      }

      setIsSessionValid(true);
      setIsValidating(false);
    };

    // Initial validation
    validate();

    // Set up realtime subscription for session changes
    const setupRealtime = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const sessionToken = getCurrentSessionToken();
      if (!sessionToken) return;

      realtimeChannel = supabase
        .channel('session-changes')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'user_sessions',
            filter: `session_token=eq.${sessionToken}`
          },
          (payload) => {
            console.log('[useSessionValidator] Session update received:', payload);
            if (payload.new && payload.new.is_active === false) {
              // Session was deactivated - but retry validation first
              validate();
            }
          }
        )
        .subscribe();
    };

    setupRealtime();

    // Periodic validation (every 15 minutes instead of 5 to reduce load)
    const intervalId = setInterval(() => {
      validate();
    }, 15 * 60 * 1000);

    return () => {
      mounted = false;
      clearInterval(intervalId);
      if (realtimeChannel) {
        supabase.removeChannel(realtimeChannel);
      }
    };
  }, [handleInvalidSession]);

  return { isValidating, isSessionValid };
}
